import { LoadingOverlay } from '@mantine/core'
import { Button } from 'components/Button/Button'
import { translate } from 'i18n/i18n'
import { useMemo, useState } from 'react'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TIrrigationZoneEmitterId } from 'stores/fieldAssetStore/helperTypes'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { getEmitterConfiguration } from '../_utils/getEmitterConfiguration'
import { getSavedFlowRate } from './_utils/getSavedFlowRate'
import { handleUpdateEmitterConfiguration } from './_utils/handleUpdateEmitterConfiguration'
import { EmitterConfigurationHeader } from './EmitterConfigurationHeader/EmitterConfigurationHeader'
import { EmitterConfigurationList } from './EmitterConfigurationList/EmitterConfigurationList'
import { useEmitterConfigurationUnit } from './hooks/useEmitterConfigurationUnit'
import { ModalDrawerBulkEdit } from './ModalDrawerBulkEdit/ModalDrawerBulkEdit'
import { ModalDrawerRateCalculator } from './ModalDrawerRateCalculator/ModalDrawerRateCalculator'

export type ModifiedZone = {
  emitterType: string
  id: TIrrigationZoneEmitterId
  zoneId: number
  propertyId: number
  flowRate: number
}

export const EmitterConfiguration = () => {
  const [rateCalculatorOpened, setRateCalculatorOpened] = useState(false)
  const [bulkEditOpened, setBulkEditOpened] = useState(false)
  const [modifiedZones, setModifiedZones] = useState<ModifiedZone[]>([])
  const [loadingSave, setLoadingSave] = useState(false)
  const isWideScreen = useIsWideScreen()

  const { properties } = fieldAssetStore.useSelector((s) => ({
    properties: s?.properties ?? [],
  }))

  const { permissions } = userDetailsStore.useSelector((s) => ({
    permissions: s.permissions,
  }))

  const emitterConfigurations = useMemo(
    () => getEmitterConfiguration(properties, permissions),
    [properties, permissions],
  )

  const buttonDisabled = modifiedZones.length === 0
  const isError = modifiedZones.some((zone) => zone.flowRate <= 0)
  const { dbUnitPerHour } = useEmitterConfigurationUnit()

  if (loadingSave) {
    return <LoadingOverlay visible />
  }

  const optimisticUpdate = () => {
    fieldAssetStore.setState((prev) => {
      let newProperties = { ...prev.properties }

      modifiedZones.forEach((zone) => {
        const { propertyId, flowRate, id } = zone

        if (newProperties?.[propertyId]?.irrigationZoneEmitters?.[id]?.flowRate) {
          newProperties = {
            ...newProperties,
            [propertyId]: {
              ...newProperties[propertyId],
              irrigationZoneEmitters: {
                ...newProperties[propertyId].irrigationZoneEmitters,
                [id]: {
                  ...(newProperties?.[propertyId]?.irrigationZoneEmitters?.[id] || {}),
                  flowRate: getSavedFlowRate(flowRate, dbUnitPerHour),
                },
              },
            },
          }
        }
      })

      return {
        ...prev,
        properties: newProperties,
      }
    })

    setModifiedZones([])
  }

  const handleSave = async () => {
    const params = modifiedZones.map((zone) => ({
      ...zone,
      unitPerHour: dbUnitPerHour,
    }))

    setLoadingSave(true)

    await handleUpdateEmitterConfiguration(params, optimisticUpdate)

    setLoadingSave(false)
  }

  return (
    <>
      <EmitterConfigurationHeader
        onRateCalculatorButtonClick={() => setRateCalculatorOpened(true)}
        onBulkEditButtonClick={() => setBulkEditOpened(true)}
      />
      <EmitterConfigurationList
        setModifiedZones={setModifiedZones}
        modifiedZones={modifiedZones}
        emitterConfigurations={emitterConfigurations}
      />
      <div
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: colors.grey50,
          position: 'fixed',
          width: isWideScreen ? 'calc(100% - 400px)' : '100%',
          bottom: 0,
          left: isWideScreen ? 400 : 0,
          padding: '10px 20px',
          paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
          border: `1px solid ${colors.grey200}`,
        }}
      >
        <Button variant="secondary" disabled={buttonDisabled} onClick={() => setModifiedZones([])}>
          {translate.phrases.banyanApp('Cancel')}
        </Button>
        <Button variant="primary" disabled={buttonDisabled || isError} onClick={handleSave}>
          {translate.phrases.banyanApp('Save')}
        </Button>
      </div>
      <ModalDrawerBulkEdit
        opened={bulkEditOpened}
        handleOnClose={() => setBulkEditOpened(false)}
        onRateCalculatorButtonClick={() => setRateCalculatorOpened(true)}
        emitterConfigurations={emitterConfigurations}
      />
      <ModalDrawerRateCalculator
        opened={rateCalculatorOpened}
        handleOnClose={() => setRateCalculatorOpened(false)}
      />
    </>
  )
}
